import {Component, EventEmitter, Input, OnInit, Output, SimpleChanges} from '@angular/core';
import {UserDefinedWidget} from '@models/pages/user-defined-page/user-defined-widget';
import {UserDefinedWidgetType} from '@constants/pages/user-defined-page/user-defined-widget-type';
import {Signal} from '@models/dashboard/signal';
import {ChartsConfigurationHelperService} from '@services/charts/helper/charts-configuration-helper.service';
import {ChartGaugeType} from '@constants/pages/user-defined-page/chart-gauge-type';
import {ChartSignalEntry} from '@models/pages/user-defined-page/chart-signal-entry';
import {UserDefinedChartDetails} from '@models/pages/user-defined-page/user-defined-chart-details';

@Component({
    selector: 'scitis-gauge-widget-configuration',
    templateUrl: './gauge-widget-configuration.component.html',
    styleUrls: ['./gauge-widget-configuration.component.scss'],
    standalone: false
})
export class GaugeWidgetConfigurationComponent implements OnInit {

  @Input()
  userDefinedWidget: UserDefinedWidget = new UserDefinedWidget();
  @Input()
  machineSignals: Signal[] = [];

  @Input()
  isTemplateWidget: boolean = false;

  @Output()
  isConfigurationValid: EventEmitter<boolean> = new EventEmitter<boolean>();

  allUsableSignals: Signal[] = [];
  hasDeletedSignalsMap: Map<string, boolean> = new Map<string, boolean>();
  public selectedSignalId: number = -1;

  constructor(private chartsConfigurationHelperService: ChartsConfigurationHelperService) {
    this.chartsConfigurationHelperService.hasDeletedSignals$.subscribe(hasDeletedSignals => {
      this.hasDeletedSignalsMap = hasDeletedSignals;
    });
  }

  public get isGaugeWidget(): boolean {
    return this.userDefinedWidget.widgetDetails.widgetType === UserDefinedWidgetType.GAUGE;
  }

  public get isModernGauge() {
    this.initializeDefaultWidgetDetails();
    return this.userDefinedWidget.userDefinedChartDetails.signals[0].gaugeType === ChartGaugeType.MODERN;
  }

  public get isClassicGauge() {
    this.initializeDefaultWidgetDetails();
    return this.userDefinedWidget.userDefinedChartDetails.signals[0].gaugeType === ChartGaugeType.CLASSIC;
  }

  ngOnChanges(changes: SimpleChanges) {
      if(changes?.userDefinedChartDetails && this.allUsableSignals.length > 0) {
        this.chartsConfigurationHelperService.hasDeletedSignals$.next(this.hasDeletedSignalsMap.set(String(this.userDefinedWidget.id), this.hasDeletedSignals()));
        this.hasDeletedSignals() ? this.isConfigurationValid.emit(false) : this.isConfigurationValid.emit(true);
      }
      if(this.allUsableSignals && (this.userDefinedWidget.userDefinedChartDetails.signals?.length ?? 0) > 0) {
        this.chartsConfigurationHelperService.hasDeletedSignals$.next(this.hasDeletedSignalsMap.set(String(this.userDefinedWidget.id), this.hasDeletedSignals()));
        this.hasDeletedSignals() ? this.isConfigurationValid.emit(false) : this.isConfigurationValid.emit(true);
      }
    }

  ngOnInit(): void {
    this.userDefinedWidget.userDefinedChartDetails.signals ??= [];
    this.allUsableSignals = [...this.chartsConfigurationHelperService.filterMisconfiguredOrUnusableSignals(this.machineSignals)];
    if (this.userDefinedWidget && this.userDefinedWidget.userDefinedChartDetails && this.userDefinedWidget.userDefinedChartDetails.signals && this.userDefinedWidget.userDefinedChartDetails.signals.length > 0) {
      const chartSignalEntry: ChartSignalEntry = this.userDefinedWidget.userDefinedChartDetails.signals[0];
      if (this.isTemplateWidget) {
        this.selectedSignalId = this.allUsableSignals.find(s => s.name.toLowerCase() === chartSignalEntry.signalName.toLowerCase())!.id;
      } else {
        this.selectedSignalId = chartSignalEntry.signalId;
      }
      this.chartsConfigurationHelperService.hasDeletedSignals$.next(this.hasDeletedSignalsMap.set(String(this.userDefinedWidget.id), this.hasDeletedSignals()));
      this.isConfigurationValid.emit(true);
    } else {
      this.isConfigurationValid.emit(false);
    }
  }

  selectModernGaugeStyle() {
    this.initializeDefaultWidgetDetails();
    this.userDefinedWidget.userDefinedChartDetails.signals[0].gaugeType = ChartGaugeType.MODERN;
  }

  selectClassicGaugeStyle() {
    this.initializeDefaultWidgetDetails();
    this.userDefinedWidget.userDefinedChartDetails.signals[0].gaugeType = ChartGaugeType.CLASSIC;
  }

  onSignalSelect($event: Signal | Signal[]) {
    if (!$event) {
      this.isConfigurationValid.emit(false);
      return;
    }

    const selectedSignal: Signal = Array.isArray($event) ? $event[0] : $event;

    this.initializeDefaultWidgetDetails();

    this.selectedSignalId = selectedSignal.id;
    this.userDefinedWidget.userDefinedChartDetails.signals[0].signalId = selectedSignal.id;
    this.userDefinedWidget.userDefinedChartDetails.signals[0].label = selectedSignal.label;
    this.userDefinedWidget.userDefinedChartDetails.signals[0].signalName = selectedSignal.name;
    this.userDefinedWidget.userDefinedChartDetails.signals[0].signalType = selectedSignal.type;
    this.userDefinedWidget.userDefinedChartDetails.signals[0].displayedText = selectedSignal.label;
    this.userDefinedWidget.userDefinedChartDetails.signals[0].scale = selectedSignal.scale;
    this.userDefinedWidget.userDefinedChartDetails.signals[0].unit = selectedSignal.unit;
    this.userDefinedWidget.userDefinedChartDetails.signals[0].precision = selectedSignal.decimals;
    this.chartsConfigurationHelperService.hasDeletedSignals$.next(this.hasDeletedSignalsMap.set(String(this.userDefinedWidget.id), this.hasDeletedSignals()));

    this.isConfigurationValid.emit(true);
  }

  hasDeletedSignals() {
    return (this.userDefinedWidget.userDefinedChartDetails.signals ?? []).some(signal =>
      signal.signalId > 0 && !this.allUsableSignals.find(s => s.id == signal.signalId)
    );
  }

  private initializeDefaultWidgetDetails() {
    if (!this.isGaugeWidget) {
      return;
    }

    if (!this.userDefinedWidget) {
      this.userDefinedWidget = new UserDefinedWidget();
    }

    if (!this.userDefinedWidget.userDefinedChartDetails) {
      this.userDefinedWidget.userDefinedChartDetails = new UserDefinedChartDetails();
    }

    if (!this.userDefinedWidget.userDefinedChartDetails.signals || this.userDefinedWidget.userDefinedChartDetails.signals.length === 0) {
      this.userDefinedWidget.userDefinedChartDetails.signals = [new ChartSignalEntry()];
    }
  }
}
